import { ProductResultDto } from "src/dtos/productResult/ProductResultDto";
import { formatDate, toLocalDateTime } from "src/utils/string/stringUtil";

// 상수 정의
export const SUBTOTAL_LEVELS = {
    ITEM: 1,
    DATE: 2,
    DETAIL: 3,
} as const;

export const SUBTOTAL_TYPES = {
    ITEM: 'item',
    DATE: 'date',
} as const;

export interface KSK2070_R01Dto extends ProductResultDto {
    compNm: string;
    orderTypeNm: string;
    workplaceNm: string;
    jobTeamNm: string;
    itemSpec: string;
    itemUnit: string;
    empNm: string;
    badQty: number;
    acceptQty: number;
    brandCd: string;
    brandNm: string;
    
    // 백엔드 추가 필드들
    startDate?: string;
    endDate?: string;
    codeEntity?: any | null;
}

/**
 * 품목별 소계 행 데이터 인터페이스
 */
export interface ItemSubtotalRow extends KSK2070_R01Dto {
    count: number;           // 해당 품목의 총 행 개수
    isSubtotal: true;       // 소계 행 식별자
    subtotalType: typeof SUBTOTAL_TYPES.ITEM;   // 소계 타입: 'item'
    level: typeof SUBTOTAL_LEVELS.ITEM;        // 계층 레벨 (1: 품목별)
}

/**
 * 생산일자별 소계 행 데이터 인터페이스
 */
export interface DateSubtotalRow extends KSK2070_R01Dto {
    count: number;           // 해당 일자의 행 개수
    isSubtotal: true;       // 소계 행 식별자
    subtotalType: typeof SUBTOTAL_TYPES.DATE;   // 소계 타입: 'date'
    level: typeof SUBTOTAL_LEVELS.DATE;        // 계층 레벨 (2: 생산일자별)
    dateCount: number;      // 해당 일자의 행 개수
}

/**
 * 그리드에 표시할 통합 데이터 타입
 */
export type GridRowData = 
    | ItemSubtotalRow 
    | DateSubtotalRow
    | (KSK2070_R01Dto & { isSubtotal: false; level: typeof SUBTOTAL_LEVELS.DETAIL });

/**
 * 품목별 소계 계산 데이터 인터페이스
 */
export interface SubtotalData {
    itemCd: string;        // 품목코드
    itemSpec: string;      // 품명
    itemUnit: string;      // 단위
    count: number;         // 해당 품목의 행 개수
    orderQty: number;      // 지시량 합계
    productQty: number;    // 생산량 합계
    badQty: number;        // 불량수량 합계
    acceptQty: number;     // 실적수량 합계
}

/**
 * 생산일자별 소계 계산 데이터 인터페이스
 */
export interface DateSubtotalData extends SubtotalData {
    productDt: string | undefined;   // 생산일자
    dateCount: number;     // 해당 일자의 행 개수
}

/**
 * 소계 행 타입 확인 헬퍼 함수들 (최적화된 버전)
 */
export const isSubtotalRow = (row: GridRowData): row is ItemSubtotalRow | DateSubtotalRow => {
    return row.isSubtotal === true;
};

export const isItemSubtotalRow = (row: GridRowData): row is ItemSubtotalRow => {
    return isSubtotalRow(row) && row.subtotalType === SUBTOTAL_TYPES.ITEM;
};

export const isDateSubtotalRow = (row: GridRowData): row is DateSubtotalRow => {
    return isSubtotalRow(row) && row.subtotalType === SUBTOTAL_TYPES.DATE;
};

/**
 * 생산 실적 관리의 단일 기본 데이터 (신규 행 추가시 사용)
 */
export const defaultKSK2070_R01Dto: KSK2070_R01Dto = {
    valueIndex: 0,
    orderIndex: 0,
    orderIndexSeq: 0,

	productResultNo: '',
	compCd: '',
	compNm: '',
    productDt: formatDate(new Date()),
	orderType: '',
	workplaceCd: '',
	workplaceNm: '',
	jobTeam: '',
	itemCd: '',
	itemStatus: '',
	itemNm: '',
	itemSpec: '',
	itemUnit: '',
	orderQty: 0,
	productEmp: '',
	productEmpNm: '',
	productQty: 0,
	badQty: 0,
	acceptQty: 0,
	logicCenter: '',
	warehouseCd: '',
	warehouseNm: '',
	basOrderType: '',
	basOrderNo: '',
	basOrderItemNo: '',
    workStartDt: toLocalDateTime(new Date()),
	workEndDt: undefined,
	inspectQty: 0,
	inspectYn: 'N',
	kpiYn: '',
	remark: '',

	orderTypeNm: '',
	jobTeamNm: '',
	empNm: '',
	brandCd: '',
	brandNm: '',

    inserted: false,
    isModified: false,
    
    // 백엔드 추가 필드들
    startDate: undefined,
    endDate: undefined,
    insertUser: undefined,
    insertForm: undefined,
    insertDt: undefined,
    updateUser: undefined,
    updateForm: undefined,
    updateDt: undefined,
    codeEntity: null,
    productOrderNo: '',
};
